const STORAGE_KEYS = {
  session: "guestSession",
  sessionId: "guestSessionId",
  roomId: "guestRoomId",
  guestName: "guestName",
  validatedAt: "guestSessionValidatedAt",
};

export const GUEST_SESSION_VALIDATION_TTL_MS = 30000;

const firstNonEmpty = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") ?? "";

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const normalizeGuestSession = (sessionData = {}) => {
  const room = sessionData?.room || sessionData?.Room || null;
  const guestProfile = sessionData?.guestProfile || sessionData?.GuestProfile || null;

  return {
    id:
      sessionData?.id ||
      sessionData?.Id ||
      sessionData?.sessionId ||
      sessionData?.SessionId ||
      null,

    sessionId:
      sessionData?.sessionId ||
      sessionData?.SessionId ||
      sessionData?.id ||
      sessionData?.Id ||
      null,

    roomId:
      sessionData?.roomId ||
      sessionData?.RoomId ||
      room?.id ||
      room?.Id ||
      null,

    roomName:
      sessionData?.roomName ||
      sessionData?.RoomName ||
      room?.name ||
      room?.Name ||
      null,

    room,

    guestName:
      firstNonEmpty(guestProfile?.fullName, guestProfile?.FullName, sessionData?.guestName, sessionData?.GuestName) ||
      "Guest",

    guestProfileId:
      firstNonEmpty(sessionData?.guestProfileId, sessionData?.GuestProfileId, guestProfile?.id, guestProfile?.Id) ||
      null,

    guestProfile,

    guestEmail:
      firstNonEmpty(guestProfile?.email, guestProfile?.Email, sessionData?.guestEmail, sessionData?.GuestEmail),

    guestPhone:
      firstNonEmpty(guestProfile?.phoneNumber, guestProfile?.PhoneNumber, sessionData?.guestPhone, sessionData?.GuestPhone),

    guestAddress:
      firstNonEmpty(guestProfile?.address, guestProfile?.Address, sessionData?.guestAddress, sessionData?.GuestAddress),

    country:
      firstNonEmpty(guestProfile?.country, guestProfile?.Country, sessionData?.country, sessionData?.Country),

    issuingCountry:
      firstNonEmpty(
        sessionData?.issuingCountry,
        sessionData?.IssuingCountry,
        guestProfile?.country,
        guestProfile?.Country,
        guestProfile?.nationality,
        guestProfile?.Nationality,
        sessionData?.nationality,
        sessionData?.Nationality
      ),

    nationality:
      firstNonEmpty(guestProfile?.nationality, guestProfile?.Nationality, sessionData?.nationality, sessionData?.Nationality),

    documentType:
      firstNonEmpty(guestProfile?.documentType, guestProfile?.DocumentType, sessionData?.documentType, sessionData?.DocumentType),

    documentNumber:
      firstNonEmpty(guestProfile?.documentNumber, guestProfile?.DocumentNumber, sessionData?.documentNumber, sessionData?.DocumentNumber),

    dateOfBirth:
      toDateInputValue(firstNonEmpty(guestProfile?.dateOfBirth, guestProfile?.DateOfBirth, sessionData?.dateOfBirth, sessionData?.DateOfBirth, sessionData?.dob, sessionData?.Dob, sessionData?.DOB)),

    gender:
      firstNonEmpty(guestProfile?.gender, guestProfile?.Gender, sessionData?.gender, sessionData?.Gender),

    qrImageUrl:
      sessionData?.qrImageUrl ||
      sessionData?.QrImageUrl ||
      "",

    organizationId:
      sessionData?.organizationId ||
      sessionData?.OrganizationId ||
      null,

    organizationName:
      sessionData?.organizationName ||
      sessionData?.OrganizationName ||
      null,

    hotelName:
      sessionData?.organizationName ||
      sessionData?.OrganizationName ||
      sessionData?.hotelName ||
      sessionData?.HotelName ||
      null,

    isCheckedOut:
      sessionData?.isCheckedOut === true ||
      sessionData?.IsCheckedOut === true,

    checkedOutAt:
      sessionData?.checkedOutAt ||
      sessionData?.CheckedOutAt ||
      null,

    isValid:
      sessionData?.isValid !== false &&
      sessionData?.IsValid !== false,

    isActive:
      sessionData?.isValid !== false &&
      sessionData?.IsValid !== false &&
      sessionData?.isCheckedOut !== true &&
      sessionData?.IsCheckedOut !== true,

    checkInAt:
      sessionData?.checkInAt ||
      sessionData?.CheckInAt ||
      sessionData?.checkedInAt ||
      sessionData?.CheckedInAt ||
      new Date().toISOString(),

    serviceChargeEnabled:
      sessionData?.serviceChargeEnabled ?? sessionData?.ServiceChargeEnabled ?? false,

    serviceChargeRate:
      Number(sessionData?.serviceChargeRate ?? sessionData?.ServiceChargeRate ?? 0),

    tdsRateEnabled:
      sessionData?.tdsRateEnabled ?? sessionData?.TdsRateEnabled ?? false,

    tdsRate:
      Number(sessionData?.tdsRate ?? sessionData?.TdsRate ?? 0),

    vatRateEnabled:
      sessionData?.vatRateEnabled ??
      sessionData?.VatRateEnabled ??
      sessionData?.vatEnabled ??
      sessionData?.VatEnabled ??
      false,

    vatRate:
      Number(sessionData?.vatRate ?? sessionData?.VatRate ?? 0),
  };
};

export const saveGuestSession = (sessionData) => {
  const session = normalizeGuestSession(sessionData);

  sessionStorage.setItem(STORAGE_KEYS.session, JSON.stringify(session));
  sessionStorage.setItem(STORAGE_KEYS.sessionId, session.sessionId || "");
  sessionStorage.setItem(STORAGE_KEYS.roomId, session.roomId || "");
  sessionStorage.setItem(STORAGE_KEYS.guestName, session.guestName || "Guest");
  sessionStorage.setItem(STORAGE_KEYS.validatedAt, String(Date.now()));

  return session;
};

export const clearGuestSession = () => {
  Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
  sessionStorage.removeItem("guestSessionToken");
};

export const getStoredGuestSession = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.session);
    return stored ? normalizeGuestSession(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
};

export const isStoredGuestSessionFresh = () => {
  const validatedAt = Number(sessionStorage.getItem(STORAGE_KEYS.validatedAt) || 0);
  return Boolean(
    validatedAt &&
      Date.now() - validatedAt < GUEST_SESSION_VALIDATION_TTL_MS
  );
};

export const getStoredGuestSessionId = () =>
  sessionStorage.getItem(STORAGE_KEYS.sessionId) || "";
